import React from "react";
import { css } from "@emotion/css";
import { MathMarkdown } from "../../components/MathMarkdown";
import { PendingIndicator } from "../../components/PendingIndicator";

/**
 * The term definition modal.  Opened when the learner highlights a term
 * and taps "Dive deeper": the coach streams a short, calibrated
 * definition here.  Two actions:
 *   • Go deeper → open a nested deep-dive scope on the term.
 *   • Close     → dismiss (returns to the live step).
 */

const backdropStyle = css`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: var(--color-backdrop, rgba(0, 0, 0, 0.45));
  backdrop-filter: blur(2px);

  @media (min-width: 640px) {
    align-items: center;
  }
`;

const panelStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 520px;
  max-height: 80vh;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  border: 1px solid var(--color-border-subtle);
  border-radius: 18px 18px 0 0;
  box-shadow: 0 -12px 40px -16px var(--color-shadow-strong, rgba(0, 0, 0, 0.5));
  overflow: hidden;

  @media (min-width: 640px) {
    border-radius: 18px;
  }
`;

const headerStyle = css`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 16px 18px 8px;
`;

const eyebrowStyle = css`
  font-size: 0.7em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const termStyle = css`
  font-size: 1.15em;
  font-weight: 700;
  color: var(--color-text);
  word-break: break-word;
`;

const bodyStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 4px 18px 16px;
  font-size: 0.95em;
  line-height: 1.6;

  p { margin: 0.4em 0; }
  p:first-of-type { margin-top: 0; }
  strong { font-weight: 600; }
  code {
    background: var(--color-bg-raised);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.92em;
  }
`;

const footerStyle = css`
  display: flex;
  gap: 10px;
  padding: 12px 18px calc(14px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--color-border-subtle);
`;

const buttonBase = css`
  flex: 1;
  padding: 11px 16px;
  border-radius: 12px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const closeButtonStyle = css`
  ${buttonBase};
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  &:hover:not(:disabled) { background: var(--color-bg-subtle); color: var(--color-text); }
`;

const deeperButtonStyle = css`
  ${buttonBase};
  border: none;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  &:hover:not(:disabled) { background: var(--color-primary-hover); }
`;

interface TermPreviewOverlayProps {
  term: string;
  definition: string;
  streaming: boolean;
  onGoDeeper: () => void;
  onClose: () => void;
}

export const TermPreviewOverlay: React.FC<TermPreviewOverlayProps> = ({
  term,
  definition,
  streaming,
  onGoDeeper,
  onClose,
}) => {
  return (
    <div className={backdropStyle} onClick={onClose} data-testid="term-preview-backdrop">
      <div
        className={panelStyle}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Definition of ${term}`}
        data-testid="term-preview"
      >
        <div className={headerStyle}>
          <span className={eyebrowStyle}>Definition</span>
          <span className={termStyle} data-testid="term-preview-term">{term}</span>
        </div>
        <div className={bodyStyle} data-testid="term-preview-definition">
          {definition ? (
            <MathMarkdown>{definition}</MathMarkdown>
          ) : (
            <PendingIndicator variant="inline" label="Defining…" />
          )}
        </div>
        <div className={footerStyle}>
          <button
            type="button"
            className={closeButtonStyle}
            onClick={onClose}
            data-testid="term-preview-close"
          >
            Close
          </button>
          <button
            type="button"
            className={deeperButtonStyle}
            onClick={onGoDeeper}
            disabled={streaming}
            data-testid="term-preview-go-deeper"
          >
            Go deeper →
          </button>
        </div>
      </div>
    </div>
  );
};
